import React, { useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import SonyModel from '../components/SonyModel';
import './LaptopViewer.css';
import { FaWhatsapp } from "react-icons/fa";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Form } from 'react-bootstrap';
import logo from '../assets/TT logo.png';
import "driver.js/dist/driver.css";
import { driver } from "driver.js";
import { Link } from 'react-router-dom';

// Map raw model part names → user-friendly aliases
const partNameAliases = {
  "Eject_Button-_1": "Eject Button",
  "Power_ON_Button_-_2": "Power Button",
  "Front_C_port_-_3": "Front USB-C Port (Left)",
  "Front_C_port_-_4": "Front USB-C Port (Right)",
  "Front_USB_port_-_5": "Front USB Port 1",
  "Front_USB_port_-_6": "Front USB Port 2",
  "Rear_USB_port_-_7": "Rear USB Port 1",
  "Rear_USB_port_-_8": "Rear USB Port 2",
  "Rear_USB_Pin_-_9": "Rear USB Pin",
  "LAN_port_-10": "LAN Port",
  "HDMI_pin_-11": "HDMI Pin",
  "HDMI_Port_-12": "HDMI Port",
  "Power_in_Connector_-13": "Power Connector",
  "Blue_Light_Death_-14": "Blue Light of Death",
  "Joy_con_-15": "Controller (Joycon)",
  "CD_tray_-16": "Disc Tray"
};

// Issues mapped to ALIAS (not raw names)
const partIssues = {
  "Power Button": [
    "Power button unresponsive",
    "Random shutdowns",
    "No power (dead console)"
  ],
  "Eject Button": [
    "Disc tray not opening (PS2/PS3)",
    "Eject button not responding"
  ],
  "Disc Tray": [
    "Disc tray not opening",
    "Game disc scratched or not reading"
  ],
  "Front USB-C Port (Left)": [
    "Port not working",
    "Port broken"
  ],
  "Front USB-C Port (Right)": [
    "Port not working",
    "Port broken"
  ],
  "Front USB Port 1": [
    "Port not working",
    "Port broken"
  ],
  "Front USB Port 2": [
    "Port not working",
    "Port broken"
  ],
  "Rear USB Port 1": [
    "Port not working",
    "Port broken"
  ],
  "Rear USB Port 2": [
    "Port not working",
    "Port broken"
  ],
  "Rear USB Pin": [
    "Port not working",
    "Pin broken"
  ],
  "LAN Port": [
    "LAN port not working",
    "LAN port broken"
  ],
  "HDMI Pin": [
    "HDMI pin damaged",
    "No display output"
  ],
  "HDMI Port": [
    "HDMI port not working",
    "HDMI port broken",
    "No display output"
  ],
  "Power Connector": [
    "Loose connection",
    "Port not working",
    "Broken connector"
  ],
  "Blue Light of Death": [
    "Console stuck on blue light",
    "No boot, no display"
  ],
  "Controller (Joycon)": [
    "Controller not detected",
    "Buttons not working",
    "Stick drift"
  ],
  "Overheating & Fan": [
    "Console gets too hot",
    "Fans running loud constantly",
    "Fan not spinning",
    "Console shuts off when hot",
    "Fan error"
  ]
};


const Sony = () => {
  const [selectedPart, setSelectedPart] = useState(null); // RAW name
  const [hoveredPart, setHoveredPart] = useState(null);   // RAW name
  const [modalShow, setModalShow] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });

  // Convert raw name → alias for UI
  const getAlias = (rawName) => partNameAliases[rawName] || rawName;

  const hoveredAlias = getAlias(hoveredPart);
  const selectedAlias = getAlias(selectedPart);

  const initialCameraPosition = [0, 0.5, 0.5];

  // Store RAW name for highlight logic
  const handlePartClick = (rawName) => {
    setSelectedPart(rawName);
  };

  const handleIssueClick = (issue) => {
    setModalContent(issue);
    setModalShow(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

const handleSubmit = () => {
    const message = `Hello, I want to report an issue:
- Device: Nintendo Switch
- Part Name: ${selectedAlias || "Not Selected"}
- Issue: ${modalContent}
- Name: ${formData.name}
- Email: ${formData.email}
- Phone: ${formData.phone}
- Location: ${formData.location}
- Issue Description: ${formData.issue}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = "971567756193"; // ✅ replace with your WhatsApp number

    // Works on both WhatsApp mobile app & WhatsApp Web (desktop)
    const url = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodedMessage}`;

    window.open(url, "_blank");

    setModalShow(false);
    setFormData({ name: "", email: "", phone: "" });
  };

  // ✅ Guided tour with driver.js
  const startTour = () => {
    const tourDriver = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      steps: [
        {
          element: ".model-canvas",
          popover: {
            title: "3D Laptop Model",
            description: "👉 Rotate and zoom the product with your mouse or touchpad.",
          },
        },
        {
          element: ".sidebar",
          popover: {
            title: "Parts List",
            description: "Click a part from this list or directly on the product model.",
          },
        },
        {
          element: ".custom-modal",
          popover: {
            title: "Submit an Issue",
            description: "Choose an issue and send it via WhatsApp for quick support!",
          },
        },
      ],
    });

    tourDriver.drive();
  };

  useEffect(() => {
    const hasSeenTour = localStorage.getItem("hasSeenTour");
    if (!hasSeenTour) {
      startTour();
      localStorage.setItem("hasSeenTour", "true");
    }
  }, []);


  return (
    <>
    <header className="top-header d-flex justify-content-between align-items-center px-3 py-2">
            <Link to={'/'}><img src={logo} style={{height: '160px', margin: '-40px -30px', padding: '0px'}} /></Link>
            <Button variant="info" onClick={startTour}>
              📖 Show Instructions
            </Button>
          </header>
      <div className="laptop-viewer-container">
        <div className="model-canvas">
          <Canvas camera={{ position: initialCameraPosition, fov: 50 }}>
            <ambientLight />
            <directionalLight position={[10, 10, 5]} />
            <SonyModel
              onPartClick={handlePartClick}
              onHover={setHoveredPart}
              selectedPart={selectedPart} // RAW name
            />
            <OrbitControls />
          </Canvas>
          {hoveredPart && <div className="tooltip">{hoveredAlias}</div>}
        </div>

        <div className="sidebar">
          {!selectedPart && (
            <>
              <h3>All Parts</h3>
              <ul>
                {Object.entries(partNameAliases).map(([rawName, alias], i) => (
                  <li
                    key={i}
                    onClick={() => handlePartClick(rawName)} // RAW name
                    style={{ cursor: 'pointer' }}
                  >
                    {alias}
                  </li>
                ))}
              </ul>
            </>
          )}

          {selectedPart && (
            <>
              <h3>{selectedAlias} Issues</h3>
              <ul>
                {(partIssues[selectedAlias] || []).map((issue, i) => (
                  <li
                    key={i}
                    onClick={() => handleIssueClick(issue)}
                    style={{ cursor: 'pointer' }}
                  >
                    {issue}
                  </li>
                ))}
              </ul>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedPart(null)}
              >
                🔙 Back to Parts
              </Button>
            </>
          )}
        </div>
      </div>

      <Modal show={modalShow} onHide={() => setModalShow(false)} centered className="custom-modal">
        <Modal.Header style={{ color: 'white' }} closeButton>
          <Modal.Title>{modalContent}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formPhone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter phone number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formLocation">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmergency">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Urgency Level"
                name="Emergency"
                value={formData.Emergency}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formIssue">
              <Form.Label>Describe Issue</Form.Label>
              <Form.Control
                as="textarea"
                rows={4} // You can adjust the number of rows as needed
                placeholder="Enter Issue Description"
                name="issue"
                value={formData.issue}
                onChange={handleInputChange}
              />
            </Form.Group>

          </Form>
          <Button variant="success" onClick={handleSubmit} className="d-flex align-items-center gap-2">
            <FaWhatsapp size={20} />
            Submit Issue on WhatsApp
          </Button>

        </Modal.Body>
        <Modal.Footer>

        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Sony;
